import { call, put, takeLatest } from 'redux-saga/effects';

import i18n from '../../i18n/i18n';
import { fetchTranslations } from '../../services/api';
import {
	i18nChangeLanguage,
	i18nLoadFailure,
	i18nLoadRequest,
	i18nLoadSuccess,
} from './i18nActions';

const MIN_EXPECTED_TRANSLATION_KEYS = 100;

const normalizeTranslations = (payload: any): Record<string, string> => {
	if (!payload) {
		return {};
	}

	if (typeof payload === 'string') {
		try {
			return normalizeTranslations(JSON.parse(payload));
		} catch {
			return {};
		}
	}

	if (Array.isArray(payload)) {
		return payload.reduce<Record<string, string>>((acc, item) => {
			if (!item || typeof item !== 'object') {
				return acc;
			}

			const key = item.key ?? item.code ?? item.name ?? item.slug;
			const value = item.value ?? item.translation ?? item.text ?? item.label;

			if (typeof key === 'string' && typeof value === 'string' && value.trim().length > 0) {
				acc[key] = value;
			}

			return acc;
		}, {});
	}

	if (typeof payload !== 'object') {
		return {};
	}

	return Object.entries(payload).reduce<Record<string, string>>((acc, [key, value]) => {
		if (typeof value === 'string' && value.trim().length > 0) {
			acc[key] = value;
		}

		return acc;
	}, {});
};

function* ensureEnglishFallbackBundle() {
	const existingEnBundle = i18n.getResourceBundle('en', 'translation') as
		| Record<string, string>
		| undefined;

	if (existingEnBundle && Object.keys(existingEnBundle).length >= MIN_EXPECTED_TRANSLATION_KEYS) {
		return;
	}

	try {
		const enPayload: Record<string, string> = yield call(fetchTranslations, 1);
		const enBundle = normalizeTranslations(enPayload);

		if (Object.keys(enBundle).length) {
			i18n.addResourceBundle('en', 'translation', enBundle, true, true);
		}
	} catch (error: any) {
		console.warn('[i18n] Failed loading EN fallback bundle:', error?.message);
	}
}

function* loadTranslationsWorker(action: ReturnType<typeof i18nLoadRequest>) {
	try {
		const { langId, langCode } = action.payload;

		if (langCode !== 'en') {
			yield call(ensureEnglishFallbackBundle);
		}

		const payload: Record<string, string> = yield call(fetchTranslations, langId);
		const bundle = normalizeTranslations(payload);
		console.warn(`[i18n] Loaded ${Object.keys(bundle).length} keys for ${langCode}`);

		if (!Object.keys(bundle).length) {
			console.warn(`[i18n] Empty bundle for ${langCode} (langId=${langId})`);
		}

		i18n.addResourceBundle(langCode, 'translation', bundle, true, true);

		yield call([i18n, i18n.changeLanguage], langCode);

		yield put(i18nLoadSuccess({ langCode, bundle }));
	} catch (e: any) {
		yield put(i18nLoadFailure(e?.message || 'Failed to load translations'));
	}
}

function* changeLanguageWorker(action: ReturnType<typeof i18nChangeLanguage>) {
	try {
		const { langCode } = action.payload;
		yield call([i18n, i18n.changeLanguage], langCode);
	} catch {}
}

export function* i18nRootSaga() {
	yield takeLatest(i18nLoadRequest.type, loadTranslationsWorker);
	yield takeLatest(i18nChangeLanguage.type, changeLanguageWorker);
}
